import {
  MdCalendarToday,
  MdAttachMoney,
  MdInbox,
  MdCheckCircle,
  MdCancel,
  MdNotifications,
} from 'react-icons/md';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';

const ICON_SIZE = 20;

export function parseNotificationMetadata(notif) {
  const raw = notif?.dataJson ?? notif?.metadata ?? notif?.data;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function getBookingIdFromNotification(notif) {
  const meta = parseNotificationMetadata(notif);
  const nested =
    meta.booking && typeof meta.booking === 'object'
      ? meta.booking
      : meta.payload && typeof meta.payload === 'object'
        ? meta.payload
        : null;

  const candidates = [
    meta.booking_id,
    meta.bookingId,
    meta.bnb_booking_id,
    meta.bnbBookingId,
    nested?.booking_id,
    nested?.bookingId,
    nested?.id,
    notif.targetId,
    notif.target_id,
    meta.target_id,
    meta.targetId,
    meta.entity_id,
    meta.entityId,
    notif.entityId,
    notif.entity_id,
    notif.objectId,
    notif.object_id,
    meta.object_id,
    meta.objectId,
    notif.resourceId,
    notif.resource_id,
    meta.resource_id,
    meta.resourceId,
    notif.referenceId,
    notif.reference_id,
    meta.reference_id,
    meta.referenceId,
    meta.reference_entity_id,
    meta.referenceEntityId,
    notif.referenceEntityId,
    notif.businessId,
    meta.business_id,
    meta.businessId,
  ];

  for (const value of candidates) {
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  const link = meta.link || meta.url || meta.deepLink || notif.link || notif.url;
  if (link) {
    const match = String(link).match(/bookings\/([^/?&#]+)/i);
    if (match?.[1]) return match[1];
  }

  return null;
}

export function getUnitRequestIdFromNotification(notif) {
  const meta = parseNotificationMetadata(notif);
  const nested =
    meta.unit_request && typeof meta.unit_request === 'object'
      ? meta.unit_request
      : meta.unitRequest && typeof meta.unitRequest === 'object'
        ? meta.unitRequest
        : meta.request && typeof meta.request === 'object'
          ? meta.request
          : null;

  const candidates = [
    meta.unit_request_id,
    meta.unitRequestId,
    meta.request_id,
    meta.requestId,
    nested?.unit_request_id,
    nested?.unitRequestId,
    nested?.request_id,
    nested?.requestId,
    nested?.id,
    notif.targetId,
    notif.target_id,
    meta.target_id,
    meta.targetId,
    meta.entity_id,
    meta.entityId,
    notif.entityId,
    notif.entity_id,
    notif.objectId,
    notif.object_id,
    meta.object_id,
    meta.objectId,
    notif.resourceId,
    notif.resource_id,
    meta.resource_id,
    meta.resourceId,
    notif.referenceId,
    notif.reference_id,
    meta.reference_id,
    meta.referenceId,
  ];

  for (const value of candidates) {
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  const link = meta.link || meta.url || meta.deepLink || notif.link || notif.url;
  if (link) {
    const match = String(link).match(/unit-requests\/([^/?&#]+)/i);
    if (match?.[1]) return match[1];
  }

  return null;
}

export function getMoveOutAssignmentIdFromNotification(notif) {
  const meta = parseNotificationMetadata(notif);
  const nested =
    meta.move_out_request && typeof meta.move_out_request === 'object'
      ? meta.move_out_request
      : meta.moveOutRequest && typeof meta.moveOutRequest === 'object'
        ? meta.moveOutRequest
        : meta.assignment && typeof meta.assignment === 'object'
          ? meta.assignment
          : null;

  const candidates = [
    meta.assignment_id,
    meta.assignmentId,
    meta.move_out_request_id,
    meta.moveOutRequestId,
    nested?.assignment_id,
    nested?.assignmentId,
    nested?.id,
    notif.targetId,
    notif.target_id,
    meta.target_id,
    meta.targetId,
    meta.entity_id,
    meta.entityId,
    notif.entityId,
    notif.entity_id,
    notif.objectId,
    notif.object_id,
    meta.object_id,
    meta.objectId,
    notif.referenceId,
    notif.reference_id,
    meta.reference_id,
    meta.referenceId,
  ];

  for (const value of candidates) {
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  const link = meta.link || meta.url || meta.deepLink || notif.link || notif.url;
  if (link) {
    const match = String(link).match(/move-out(?:-requests)?\/([^/?&#]+)/i);
    if (match?.[1]) return match[1];
    const assignmentMatch = String(link).match(/assignments\/([^/?&#]+)/i);
    if (assignmentMatch?.[1]) return assignmentMatch[1];
  }

  return null;
}

export function getMoveOutPropertyIdFromNotification(notif) {
  const meta = parseNotificationMetadata(notif);
  const nested =
    meta.move_out_request && typeof meta.move_out_request === 'object'
      ? meta.move_out_request
      : meta.moveOutRequest && typeof meta.moveOutRequest === 'object'
        ? meta.moveOutRequest
        : null;

  const candidates = [
    meta.property_id,
    meta.propertyId,
    nested?.property_id,
    nested?.propertyId,
    meta.property?.id,
    nested?.property?.id,
  ];

  for (const value of candidates) {
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  const link = meta.link || meta.url || meta.deepLink || notif.link || notif.url;
  if (link) {
    const match = String(link).match(/properties\/([^/?&#]+)/i);
    if (match?.[1]) return match[1];
  }

  return null;
}

export function isMoveOutRequestNotification(notif) {
  const type = String(notif?.type || '').toUpperCase();
  const title = String(notif?.title || '').toLowerCase();
  const body = String(notif?.body || '').toLowerCase();

  if (type.includes('MOVE') && type.includes('OUT')) return true;
  if (type === 'MOVE_OUT_REQUEST_SUBMITTED') return true;
  if (title.includes('move-out request') || title.includes('move out request')) return true;
  if (body.includes('move-out request') || body.includes('move out request')) return true;
  if (title.includes('submitted a move-out') || body.includes('submitted a move-out')) return true;
  return false;
}

export function resolveMoveOutAssignmentIdFromHint(rows, hint) {
  if (!hint || !Array.isArray(rows) || rows.length === 0) return null;

  const notifTime = hint.createdAt ? new Date(hint.createdAt).getTime() : NaN;
  if (!Number.isFinite(notifTime)) return null;

  let best = null;
  let bestDelta = Infinity;
  for (const row of rows) {
    const t = new Date(row.createdAt || row.updatedAt || 0).getTime();
    if (!Number.isFinite(t)) continue;
    const delta = Math.abs(t - notifTime);
    if (delta < bestDelta && delta <= 10 * 60 * 1000) {
      bestDelta = delta;
      best = row;
    }
  }

  const id = best?.assignmentId || best?.id;
  return id != null ? String(id) : null;
}

export function isUnitRequestNotification(notif) {
  const type = String(notif?.type || '').toUpperCase();
  const title = String(notif?.title || '').toLowerCase();
  const body = String(notif?.body || '').toLowerCase();

  if (type.includes('UNIT') && type.includes('REQUEST')) return true;
  if (type === 'UNIT_REQUEST_SUBMITTED') return true;
  if (title.includes('unit request')) return true;
  if (body.includes('unit request')) return true;
  if (title.includes('tenant applied') || body.includes('applied for')) return true;
  return false;
}

export function isUnitRequestPaymentEvidenceNotification(notif) {
  if (!isUnitRequestNotification(notif)) return false;
  const type = String(notif?.type || '').toUpperCase();
  const title = String(notif?.title || '').toLowerCase();
  const body = String(notif?.body || '').toLowerCase();
  return (
    (type.includes('PAYMENT') && !type.includes('BNB')) ||
    title.includes('payment evidence') ||
    title.includes('deposit') ||
    title.includes('first month') ||
    body.includes('payment evidence') ||
    body.includes('submitted payment')
  );
}

export function requestHasPaymentEvidence(req) {
  if (!req) return false;
  return Boolean(
    req.depositEvidenceUrl ||
    req.depositEvidenceText ||
    req.depositPaidAmount ||
    req.firstMonthEvidenceUrl ||
    req.firstMonthEvidenceText ||
    req.firstMonthPaidAmount
  );
}

/**
 * Last resort when the notification has no request id: match by time only.
 */
export function resolveUnitRequestIdFromHint(requests, hint) {
  if (!hint || !Array.isArray(requests) || requests.length === 0) return null;

  const notifTime = hint.createdAt ? new Date(hint.createdAt).getTime() : NaN;
  if (!Number.isFinite(notifTime)) return null;

  let best = null;
  let bestDelta = Infinity;
  for (const r of requests) {
    const t = new Date(r.createdAt || r.updatedAt || 0).getTime();
    if (!Number.isFinite(t)) continue;
    const delta = Math.abs(t - notifTime);
    if (delta < bestDelta && delta <= 10 * 60 * 1000) {
      bestDelta = delta;
      best = r;
    }
  }

  return best?.id != null ? String(best.id) : null;
}

export function isBnbBookingNotification(notif) {
  const type = String(notif?.type || '').toUpperCase();
  const title = String(notif?.title || '').toLowerCase();
  const body = String(notif?.body || '').toLowerCase();

  if (type.includes('BNB') && type.includes('BOOK')) return true;
  if (title.includes('bnb booking') || title.includes('booking payment')) return true;
  if (body.includes('bnb booking') || body.includes('booking fee payment')) return true;
  return false;
}

export function isBnbPaymentEvidenceNotification(notif) {
  const type = String(notif?.type || '').toUpperCase();
  const title = String(notif?.title || '').toLowerCase();
  return (type.includes('PAYMENT') && type.includes('BOOK')) || title.includes('payment evidence');
}

export function bookingHasPaymentEvidence(booking) {
  if (!booking) return false;
  return Boolean(
    booking.depositEvidenceUrl ||
    booking.depositEvidenceText ||
    booking.depositPaidAmount ||
    booking.firstMonthEvidenceUrl ||
    booking.firstMonthEvidenceText ||
    booking.firstMonthPaidAmount ||
    booking.bookingFeeEvidenceText ||
    booking.bookingFeeEvidencePhotoUrl
  );
}

/**
 * Last resort when the notification has no booking id: match by time only.
 * Does not filter by status — confirmed/rejected bookings are included.
 */
export function resolveBookingIdFromHint(bookings, hint) {
  if (!hint || !Array.isArray(bookings) || bookings.length === 0) return null;

  const notifTime = hint.createdAt ? new Date(hint.createdAt).getTime() : NaN;
  if (!Number.isFinite(notifTime)) return null;

  let best = null;
  let bestDelta = Infinity;
  for (const b of bookings) {
    const t = new Date(b.createdAt || b.updatedAt || 0).getTime();
    if (!Number.isFinite(t)) continue;
    const delta = Math.abs(t - notifTime);
    if (delta < bestDelta && delta <= 10 * 60 * 1000) {
      bestDelta = delta;
      best = b;
    }
  }

  return best?.id != null ? String(best.id) : null;
}

export function getNotificationIconElement(type, notif) {
  const iconProps = { size: ICON_SIZE, 'aria-hidden': true };

  if (isBnbBookingNotification(notif)) {
    return isBnbPaymentEvidenceNotification(notif) ? (
      <MdAttachMoney {...iconProps} />
    ) : (
      <MdCalendarToday {...iconProps} />
    );
  }

  switch (type) {
    case 'UNIT_REQUEST_SUBMITTED':
      return <MdInbox {...iconProps} />;
    case 'TENANT_SCORE_ACCESS_APPROVED':
    case 'ROLE_APPROVED':
      return <MdCheckCircle {...iconProps} />;
    case 'TENANT_SCORE_ACCESS_REJECTED':
      return <MdCancel {...iconProps} />;
    case 'PAYMENT_RECEIVED':
      return <MdAttachMoney {...iconProps} />;
    case 'CHAT_MESSAGE':
    case 'NEW_MESSAGE':
      return <IoChatbubbleEllipsesOutline {...iconProps} />;
    default:
      if (notif?.title?.toLowerCase().includes('message')) {
        return <IoChatbubbleEllipsesOutline {...iconProps} />;
      }
      return <MdNotifications {...iconProps} />;
  }
}
